import { CenterItem, RowAlignedItems } from '@components/LinearLayout';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import { getLanguage } from '@store/slices/configSlice';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@components/Button';
import WeModal from '@components/WeModal';
import { ApplyButtonWrapper } from '@composites/Filters/styles';
import { DefaultBooleanValues } from '@composites/InboundButton';
import PartnerProspectusPdfContent from '@composites/PartnerProspectusPdfContent';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  usePublicAllAgendas,
  usePublicCoLocatedEventsByEventId,
  usePublicExhibitions,
} from '@utils/apiHooks';
import { SPEAKER_PAGE_WIDTH } from '@utils/constants';
import { safeJSONParse, uniqBy } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import RegistrationForm from '../RegistrationForm';
import {
  PublicExhibitionItem,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';

interface DownloadPartnerProspectusProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const DownloadPartnerProspectus = ({
  label,
  buttonType,
  inboundValues,
  eventId,
}: DownloadPartnerProspectusProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMobileWidth();
  const language = useSelector(getLanguage);
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const { inboundCookie, actionInbound, registerInbound, shouldShowForm } =
    useInbound();

  const {
    isSuccess,
    data: coLocatedEvents,
    isLoading: isCoLocatedEventsLoading,
  } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
  });

  const selectedEventsIds = useMemo(() => {
    if (sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated)
      return coLocatedEvents?.map(e => e.id) ?? ([] as string[]);
    return [sluggedEvent?.id];
  }, [coLocatedEvents, sluggedEvent?.colocated, sluggedEvent?.id]);

  const agendas: any =
    usePublicAllAgendas({
      page: 0,
      language: sluggedEvent?.language,
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0,
      colocated: sluggedEvent?.colocated,
    }) || [];
  const agendasSessions = useMemo(() => {
    if (Array.isArray(agendas.data)) {
      return agendas.data
        ?.map((e: any) => e?.sessions)
        ?.flat()
        ?.filter((notNull: any) => notNull) as PublicSessionItem[];
    } else {
      return [];
    }
  }, [agendas]);

  const exhibitions = usePublicExhibitions({
    page: 0,
    language: sluggedEvent?.language,
    eventIds: selectedEventsIds,
    enabled:
      selectedEventsIds?.length > 0 ||
      (sluggedEvent?.colocated && isSuccess && selectedEventsIds?.length > 0),
  });

  const agendasSpeakers = useMemo(
    () =>
      uniqBy(
        agendasSessions
          ?.map(e => e?.speakers)
          ?.flat()
          ?.filter(notNull => notNull),
        'profile'
      ),
    [agendasSessions]
  );
  const allExhibitions = useMemo(
    () =>
      exhibitions
        ?.map(a => a?.data)
        ?.flat()
        ?.filter(notNull => notNull) as PublicExhibitionItem[],
    [exhibitions]
  );

  const generatePartnerProspectusPDF = useCallback(() => {
    const doc = (
      <PartnerProspectusPdfContent
        event={sluggedEvent}
        language={language}
        theme={theme}
        agendaSpeakers={agendasSpeakers}
        exhibitions={allExhibitions}
      />
    );
    printPdf(doc, `${t('partners')} - ${sluggedEvent?.name}`).catch(
      console.error
    );
  }, [agendasSpeakers, allExhibitions, language, sluggedEvent, t, theme]);

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundCookie)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundCookie, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_bp_packages',
        },
        () => {
          generatePartnerProspectusPDF();
          setShowModal(false);
        }
      );
    },
    [checkedApproval, inboundCookie, registerInbound, sluggedEvent?.id]
  );

  return (
    <>
      <Button
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding={isMobile ? '1.1rem 2.22rem' : '1.1rem 3rem'}
        width={isMobile ? '100%' : 'max-content'}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              setShowModal(true);
            } else {
              generatePartnerProspectusPDF();

              await actionInbound({
                ...JSON.parse(inboundCookie ?? '{}'),
                event: sluggedEvent?.id,
                path: window.location.href,
                action: 'download_bp_packages',
              });
            }
          })();
        }}
      />
      <WeModal
        visible={showModal}
        maxHeight="94vh"
        backgroundColor={theme.color.pureWhite}
        width={`min(${SPEAKER_PAGE_WIDTH}, 90vw)`}
        onClose={() => {
          setShowModal(false);
        }}
        footer={
          <CenterItem>
            <ApplyButtonWrapper>
              <RowAlignedItems $gap="3rem">
                <Button
                  title={t('download')}
                  backgroundColor={sluggedEvent?.design.themeColor}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius10}
                  padding="0.6rem 2.5rem"
                  width="max-content"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmit(onSubmit)}
                />
              </RowAlignedItems>
            </ApplyButtonWrapper>
          </CenterItem>
        }
      >
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <RegistrationForm
            control={control}
            errors={errors}
            inputElements={inputElements}
            setCheckedApproval={setCheckedApproval}
            checkedApproval={checkedApproval}
          />
        </form>
      </WeModal>
    </>
  );
};

export default DownloadPartnerProspectus;
